import { useCallback, useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import api from "../services/api";
import toast from "react-hot-toast";

const EVENT_OPTIONS = [
  { label: "All Events", value: "" },
  { label: "Login Success", value: "LOGIN_SUCCESS" },
  { label: "API Access", value: "API_ACCESS" },
];

export default function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [eventType, setEventType] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 1,
  });

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/audit-logs", {
        params: {
          page,
          limit: 25,
          event_type: eventType || undefined,
          start_date: startDate || undefined,
          end_date: endDate || undefined,
        },
      });

      const payload = res.data?.data || {};
      setLogs(payload.logs || []);
      setPagination(
        payload.pagination || {
          page: 1,
          limit: 25,
          total: 0,
          totalPages: 1,
        }
      );
    } catch (error) {
      console.error("Failed to fetch audit logs:", error);
      toast.error("Failed to load audit logs");
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [eventType, page, startDate, endDate]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleEventChange = (value) => {
    setPage(1);
    setEventType(value);
  };

  const clearDateFilters = () => {
    setPage(1);
    setStartDate("");
    setEndDate("");
  };

  return (
    <div className="flex min-h-screen bg-gray-100">
      <Sidebar />

      <main className="flex-1 p-6">
        <div className="bg-gradient-to-r from-green-700 to-green-500 text-white p-5 rounded-xl shadow-md mb-6">
          <h2 className="text-2xl font-bold">Audit Logs</h2>
          <p className="text-sm mt-1">Track who accessed the system and when</p>
        </div>

        <div className="bg-white p-4 rounded-xl shadow mb-6 flex flex-col gap-3">
          <div className="text-sm text-gray-700">
            Total logs: <span className="font-semibold">{pagination.total}</span>
          </div>

          <div className="flex flex-col md:flex-row md:items-center gap-3">
            <select
              value={eventType}
              onChange={(e) => handleEventChange(e.target.value)}
              className="border p-2 rounded focus:ring-2 focus:ring-green-600 outline-none"
            >
              {EVENT_OPTIONS.map((option) => (
                <option key={option.value || "all"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            <label className="text-sm text-gray-700 flex items-center gap-2">
              <span>From</span>
              <input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setPage(1);
                  setStartDate(e.target.value);
                }}
                className="border p-2 rounded focus:ring-2 focus:ring-green-600 outline-none"
              />
            </label>

            <label className="text-sm text-gray-700 flex items-center gap-2">
              <span>To</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => {
                  setPage(1);
                  setEndDate(e.target.value);
                }}
                className="border p-2 rounded focus:ring-2 focus:ring-green-600 outline-none"
              />
            </label>

            <button
              type="button"
              onClick={clearDateFilters}
              className="px-4 py-2 rounded bg-gray-200 text-gray-700"
            >
              Clear Dates
            </button>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-green-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-green-700 uppercase">Time</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-green-700 uppercase">User</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-green-700 uppercase">Role</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-green-700 uppercase">Event</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-green-700 uppercase">Endpoint</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-green-700 uppercase">IP</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                      Loading logs...
                    </td>
                  </tr>
                ) : logs.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                      No audit logs found
                    </td>
                  </tr>
                ) : (
                  logs.map((log) => (
                    <tr key={log.id} className="hover:bg-green-50 transition">
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {new Date(log.createdAt).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        <div className="font-medium">{log.user?.name || "Unknown"}</div>
                        <div className="text-xs text-gray-500">{log.user?.email || "-"}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">{log.user?.role || "-"}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{log.event_type}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{`${log.method || "-"} ${log.path || "-"}`}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{log.ip_address || "-"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-gray-600">
            Page {pagination.page} of {pagination.totalPages || 1}
          </p>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
              disabled={pagination.page <= 1 || loading}
              className="px-4 py-2 rounded bg-gray-200 text-gray-700 disabled:opacity-50"
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() => setPage((prev) => (prev < pagination.totalPages ? prev + 1 : prev))}
              disabled={pagination.page >= pagination.totalPages || loading}
              className="px-4 py-2 rounded bg-green-700 text-white disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
